import React, { useEffect, useRef, useState } from 'react';
import { relazioniAPI } from '../../lib/api';
import { Button } from '../ui/button';
import { Upload, FileText, Eye, Trash2, Loader2, Camera, Image as ImageIcon } from 'lucide-react';
import { toast } from 'sonner';
import { apiErrorMessage } from '../../lib/apiError';
import { openSignedFile } from '../../lib/openFile';
import { PhotoGallery } from '../PhotoGallery';
import { PhotoLightbox } from '../PhotoLightbox';
import { DocumentPreviewDialog } from '../DocumentPreviewDialog';

const formatBytes = (b) => {
    if (!b) return '0 B';
    const k = 1024, sizes = ['B', 'KB', 'MB'];
    const i = Math.min(Math.floor(Math.log(b) / Math.log(k)), 2);
    return `${parseFloat((b / Math.pow(k, i)).toFixed(1))} ${sizes[i]}`;
};

export const RelazioneAttachments = ({ relazioneId, canEdit }) => {
    const [docs, setDocs] = useState([]);
    const [uploading, setUploading] = useState(false);
    const [viewingId, setViewingId] = useState(null);
    const [lightbox, setLightbox] = useState(null);
    const [preview, setPreview] = useState(null);
    const inputRef = useRef(null);
    const cameraRef = useRef(null);
    const photos = docs.filter(d => d.thumb_url);

    const load = async () => {
        try { const r = await relazioniAPI.listDocuments(relazioneId); setDocs(r.data || []); } catch { /* noop */ }
    };
    useEffect(() => { if (relazioneId) load(); // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [relazioneId]);

    const onPick = async (e) => {
        const files = Array.from(e.target.files || []);
        e.target.value = '';
        if (!files.length) return;
        setUploading(true);
        let ok = 0;
        for (const f of files) {
            try { await relazioniAPI.uploadDocument(relazioneId, f); ok += 1; }
            catch (err) { toast.error(apiErrorMessage(err, `Errore caricando ${f.name}`)); }
        }
        if (ok) toast.success(`${ok} allegat${ok === 1 ? 'o caricato' : 'i caricati'}`);
        setUploading(false);
        load();
    };

    const onView = async (d) => {
        const idx = photos.findIndex(p => p.id === d.id);
        if (idx >= 0) { setLightbox(idx); return; }
        setViewingId(d.id);
        try {
            if (/^application\/pdf/.test(d.content_type || '')) {
                setPreview({ doc: d, url: null });
                const { data } = await relazioniAPI.documentLink(d.id);
                setPreview({ doc: d, url: `${process.env.REACT_APP_BACKEND_URL}${data.url}` });
                return;
            }
            await openSignedFile(() => relazioniAPI.documentLink(d.id), d.content_type);
        } catch (err) {
            setPreview(null);
            toast.error(apiErrorMessage(err, 'Impossibile aprire il file'));
        } finally { setViewingId(null); }
    };

    const onDelete = async (d) => {
        if (!window.confirm(`Eliminare "${d.filename}"?`)) return;
        try { await relazioniAPI.deleteDocument(d.id); toast.success('Allegato eliminato'); load(); }
        catch (err) { toast.error(apiErrorMessage(err, 'Errore nell\'eliminazione')); }
    };

    return (
        <div className="space-y-2 mt-1" data-testid="relazione-attachments">
            {canEdit && (
                <div className="flex gap-2">
                    <input ref={inputRef} type="file" multiple className="hidden" accept=".pdf,.xls,.xlsx,.jpg,.jpeg,.png,.heic,.webp,.csv,.doc,.docx,.txt" onChange={onPick} data-testid="relazione-attachment-input" />
                    <input ref={cameraRef} type="file" accept="image/*" capture="environment" className="hidden" onChange={onPick} data-testid="relazione-camera-input" />
                    <Button type="button" variant="outline" size="sm" onClick={() => cameraRef.current?.click()} disabled={uploading} data-testid="relazione-camera-btn">
                        <Camera size={14} className="mr-2" /> Foto
                    </Button>
                    <Button type="button" variant="outline" size="sm" onClick={() => inputRef.current?.click()} disabled={uploading} data-testid="relazione-attachment-upload">
                        {uploading ? <Loader2 size={14} className="mr-2 animate-spin" /> : <Upload size={14} className="mr-2" />} Carica file
                    </Button>
                </div>
            )}
            <PhotoGallery photos={photos} onOpen={setLightbox} />
            <PhotoLightbox photos={photos} index={lightbox} onIndex={setLightbox} onClose={() => setLightbox(null)} />
            <DocumentPreviewDialog doc={preview?.doc} url={preview?.url} onClose={() => setPreview(null)} />
            {docs.length === 0 ? (
                <p className="text-xs text-muted-foreground">Nessun allegato</p>
            ) : (
                <ul className="divide-y border rounded-sm">
                    {docs.map(d => (
                        <li key={d.id} className="flex items-center gap-2 px-3 py-1.5 text-sm" data-testid={`relazione-attachment-${d.id}`}>
                            {(d.content_type || '').startsWith('image/') ? <ImageIcon size={14} className="text-muted-foreground shrink-0" /> : <FileText size={14} className="text-muted-foreground shrink-0" />}
                            <span className="truncate flex-1">{d.filename}</span>
                            <span className="text-xs text-muted-foreground shrink-0">{formatBytes(d.size)}</span>
                            <Button type="button" variant="ghost" size="icon" className="h-7 w-7" onClick={() => onView(d)} disabled={viewingId === d.id} title="Apri" data-testid={`relazione-attachment-view-${d.id}`}>
                                {viewingId === d.id ? <Loader2 size={14} className="animate-spin" /> : <Eye size={14} />}
                            </Button>
                            {canEdit && (
                                <Button type="button" variant="ghost" size="icon" className="h-7 w-7 text-destructive" onClick={() => onDelete(d)} title="Elimina" data-testid={`relazione-attachment-delete-${d.id}`}>
                                    <Trash2 size={14} />
                                </Button>
                            )}
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
};

export default RelazioneAttachments;
